/**
 * Global call center context.
 *
 * Wraps the entire authenticated app so any candidate card can trigger an
 * outbound call, and any page can render the "incoming call" banner /
 * mini-dock when a candidate rings +33 1 62 29 09 54.
 *
 * Responsibilities:
 *   1. Fetch a Twilio Voice access token from the backend.
 *   2. Create a `Twilio.Device` and register() it for inbound calls.
 *   3. Expose `startOutboundCall(candidate)` → creates a CallLog, then
 *      connects to the TwiML app with To=candidate.phone.
 *   4. Expose `activeCall` state so a floating dock UI can render itself.
 *   5. Auto-refresh the token every ~50 min.
 *
 * Only admins and pré-recruteurs get a device; other roles get a no-op provider.
 */
import { Device } from "@twilio/voice-sdk";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";

import { api } from "./api";
import { useAuth } from "./auth";
import { hasAnyRole } from "./roles";

const CallCenterContext = createContext(null);

const CALL_ROLES = ["admin", "pre_recruteur"];

export function CallCenterProvider({ children }) {
    const { user } = useAuth();
    const deviceRef = useRef(null);
    const activeCallRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | connecting | ringing | active | ended | error
    const [twilioStatus, setTwilioStatus] = useState(null);
    const [activeCall, setActiveCall] = useState(null); // { candidate, callLogId, direction, from, phone, name }
    const [incomingCall, setIncomingCall] = useState(null); // { callObj, from, name }
    const [error, setError] = useState(null);
    const [muted, setMuted] = useState(false);
    const canCall = user && hasAnyRole(user, ...CALL_ROLES);

    /** Fetch a fresh Twilio JWT — 1h TTL, refreshed on tokenWillExpire. */
    const fetchToken = useCallback(async () => {
        const { data } = await api.get("/twilio/token");
        return data.token;
    }, []);

    /** Initialise Twilio.Device the first time the provider mounts (if allowed). */
    const initDevice = useCallback(async () => {
        if (!canCall || deviceRef.current || registering) return;
        setRegistering(true);
        setError(null);
        try {
            // Ask for mic permission proactively so the user isn't surprised
            // when the first call is placed. If they decline, we still allow
            // the token to be fetched and calls to be attempted (Twilio will
            // surface a clearer error at connect time).
            try {
                await navigator.mediaDevices.getUserMedia({ audio: true });
            } catch (_e) {
                // Non-blocking
            }
            const token = await fetchToken();
            const device = new Device(token, {
                codecPreferences: ["opus", "pcmu"],
                logLevel: "warn",
            });
            device.on("registered", () => setReady(true));
            device.on("unregistered", () => setReady(false));
            device.on("tokenWillExpire", async () => {
                try {
                    const t = await fetchToken();
                    device.updateToken(t);
                } catch (e) {
                    // silent — will re-register on next mount
                }
            });
            device.on("error", (e) => {
                setError(e?.message || "Erreur Twilio");
                setStatus("error");
            });
            device.on("incoming", (call) => {
                const from = call.parameters?.From || "";
                setIncomingCall({ callObj: call, from, name: null });
                call.on("cancel", () => setIncomingCall(null));
                call.on("disconnect", () => {
                    setIncomingCall(null);
                    setActiveCall(null);
                    setStatus("ended");
                    activeCallRef.current = null;
                });
            });
            await device.register();
            deviceRef.current = device;
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Impossible d'initialiser Twilio");
        } finally {
            setRegistering(false);
        }
    }, [canCall, fetchToken, registering]);

    // Fetch backend Twilio status once — surfaces "Deepgram OK / Twilio OK" in UI
    useEffect(() => {
        if (!user) return;
        api.get("/twilio/status")
            .then(({ data }) => setTwilioStatus(data))
            .catch(() => setTwilioStatus(null));
    }, [user]);

    // NOTE: We *don't* auto-init the device on mount — we wait for the user
    // to explicitly click "Appeler" once (which requires a user gesture in
    // most browsers for audio autoplay anyway). Once initialised, it stays
    // registered for the session.
    useEffect(() => {
        return () => {
            try {
                deviceRef.current?.destroy();
            } catch (_e) { /* ignore */ }
            deviceRef.current = null;
        };
    }, []);

    /** Launch an outbound call to a candidate. */
    const startOutboundCall = useCallback(
        async (candidate) => {
            if (!candidate) return;
            if (!canCall) {
                toast.error("Votre rôle ne permet pas d'émettre des appels");
                return;
            }
            if (!deviceRef.current) {
                await initDevice();
            }
            if (!deviceRef.current) {
                toast.error(error || "Micro non autorisé — vérifiez les permissions du navigateur puis réessayez.");
                return;
            }
            try {
                setStatus("connecting");
                // 1. Create the CallLog row server-side so the transcription
                //    pipeline can attach itself to the recording webhook.
                //    The dev safelist may reject this with HTTP 423 — that
                //    error is surfaced as a French toast below.
                const { data } = await api.post("/twilio/calls/outbound/start", {
                    candidate_id: candidate.id,
                    to_number: candidate.phone,
                });
                const call = await deviceRef.current.connect({
                    params: {
                        To: data.to_number,
                        call_log_id: data.call_log_id,
                    },
                });
                activeCallRef.current = call;
                setActiveCall({
                    candidate,
                    callLogId: data.call_log_id,
                    direction: "outbound",
                    phone: data.to_number,
                    name: `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim(),
                    startedAt: Date.now(),
                });
                setStatus("ringing");
                call.on("accept", () => setStatus("active"));
                call.on("ringing", () => setStatus("ringing"));
                call.on("mute", (isMuted) => setMuted(!!isMuted));
                call.on("disconnect", () => {
                    setStatus("ended");
                    setActiveCall(null);
                    activeCallRef.current = null;
                });
                call.on("error", (e) => {
                    toast.error(e?.message || "Erreur pendant l'appel");
                    setStatus("error");
                });
                if (data.warning) toast.warning(data.warning);
            } catch (e) {
                setStatus("error");
                const msg = e?.response?.data?.detail || e?.message || "Impossible de lancer l'appel";
                toast.error(msg);
            }
        },
        [canCall, error, initDevice],
    );

    /** Hang up the currently-active call (outbound or inbound accepted). */
    const hangup = useCallback(() => {
        try {
            activeCallRef.current?.disconnect();
        } catch (_e) { /* ignore */ }
        setActiveCall(null);
        setStatus("ended");
    }, []);

    /** Accept the currently-ringing inbound call. */
    const acceptIncoming = useCallback(() => {
        if (!incomingCall?.callObj) return;
        try {
            incomingCall.callObj.accept();
            activeCallRef.current = incomingCall.callObj;
            setActiveCall({
                candidate: null,
                callLogId: null,
                direction: "inbound",
                phone: incomingCall.from,
                name: incomingCall.name || incomingCall.from,
                startedAt: Date.now(),
            });
            setIncomingCall(null);
            setStatus("active");
            incomingCall.callObj.on("mute", (isMuted) => setMuted(!!isMuted));
            incomingCall.callObj.on("disconnect", () => {
                setActiveCall(null);
                setStatus("ended");
                activeCallRef.current = null;
            });
        } catch (e) {
            toast.error("Impossible d'accepter l'appel");
        }
    }, [incomingCall]);

    const rejectIncoming = useCallback(() => {
        try {
            incomingCall?.callObj?.reject();
        } catch (_e) { /* ignore */ }
        setIncomingCall(null);
    }, [incomingCall]);

    const toggleMute = useCallback(() => {
        if (!activeCallRef.current) return;
        const next = !muted;
        try {
            activeCallRef.current.mute(next);
            setMuted(next);
        } catch (_e) { /* ignore */ }
    }, [muted]);

    const value = useMemo(
        () => ({
            canCall,
            ready,
            registering,
            initDevice,
            status,
            error,
            twilioStatus,
            activeCall,
            incomingCall,
            startOutboundCall,
            hangup,
            acceptIncoming,
            rejectIncoming,
            toggleMute,
            muted,
        }),
        [
            canCall, ready, registering, initDevice, status, error, twilioStatus,
            activeCall, incomingCall, startOutboundCall, hangup,
            acceptIncoming, rejectIncoming, toggleMute, muted,
        ],
    );

    return (
        <CallCenterContext.Provider value={value}>
            {children}
        </CallCenterContext.Provider>
    );
}

export function useCallCenter() {
    const ctx = useContext(CallCenterContext);
    if (!ctx) {
        // Safe no-op fallback for pages that render outside the provider
        // (e.g. Login) — prevents "cannot read canCall of null" crashes.
        return {
            canCall: false,
            ready: false,
            registering: false,
            initDevice: () => {},
            status: "idle",
            error: null,
            twilioStatus: null,
            activeCall: null,
            incomingCall: null,
            startOutboundCall: () => {},
            hangup: () => {},
            acceptIncoming: () => {},
            rejectIncoming: () => {},
            toggleMute: () => {},
            muted: false,
        };
    }
    return ctx;
}
